import React from 'react';
import { Formik, Form, Field, FieldProps } from 'formik';
import * as Yup from 'yup';
import { Modal, InputField, Alert } from '../../../commons';
import { DAV_APIS } from '../../../../adapters';
import { STAFF_ROLE_OPTIONS } from '../../../../constants';

interface InviteStaffModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const schema = Yup.object().shape({
  username: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  role: Yup.string().required('Role is required'),
});

const initialValues = { username: '', email: '', role: 'admin' };

const InviteStaffModal: React.FC<InviteStaffModalProps> = ({ open, onClose, onSuccess }) => {
  const handleSubmit = async (values: typeof initialValues, { setSubmitting, resetForm }: any) => {
    try {
      await DAV_APIS.agents.inviteStaffUser(values);
      Alert({ message: 'Invitation sent successfully', variant: 'success' });
      resetForm();
      onSuccess();
      onClose();
    } catch {
      Alert({ message: 'Failed to send invitation', variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size='md' hideCancelButton hideSuccessButton>
      <div className='p-4 md:p-6'>
        <div className='mb-6'>
          <h2 className='text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight'>Invite Staff Member</h2>
          <p className='text-sm text-gray-400 mt-1'>
            An email with a link to set their password will be sent immediately.
          </p>
        </div>

        <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit}>
          {({ isSubmitting, isValid }) => (
            <Form className='space-y-5'>
              <InputField
                label='Full Name'
                name='username'
                placeholder='e.g. Jane Doe'
                customClass='!bg-gray-50 dark:!bg-[#232d3d] !text-gray-900 dark:!text-white !rounded-2xl !border-none !h-12 !px-4'
                labelClass='!text-gray-500 dark:!text-gray-400 !font-semibold !text-xs !mb-1.5'
              />
              <InputField
                label='Email Address'
                name='email'
                type='email'
                placeholder='e.g. jane@davsafaris.com'
                customClass='!bg-gray-50 dark:!bg-[#232d3d] !text-gray-900 dark:!text-white !rounded-2xl !border-none !h-12 !px-4'
                labelClass='!text-gray-500 dark:!text-gray-400 !font-semibold !text-xs !mb-1.5'
              />

              <Field name='role'>
                {({ field, meta }: FieldProps) => (
                  <div>
                    <label className='block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5'>
                      Role
                    </label>
                    <select
                      {...field}
                      className={`w-full h-12 px-4 bg-gray-50 dark:bg-[#232d3d] text-gray-900 dark:text-white rounded-2xl border-none text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 ${meta.touched && meta.error ? 'ring-2 ring-red-500' : ''}`}
                    >
                      {STAFF_ROLE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {meta.touched && meta.error && (
                      <p className='text-xs text-red-500 mt-1'>{meta.error}</p>
                    )}
                  </div>
                )}
              </Field>

              <div className='flex items-center justify-end gap-4 pt-4 border-t border-gray-100 dark:border-gray-800'>
                <button
                  type='button'
                  onClick={onClose}
                  className='text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={isSubmitting || !isValid}
                  className='px-8 py-2.5 bg-[#10b981] text-white rounded-2xl text-sm font-extrabold shadow-lg hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:scale-100 transition-all'
                >
                  {isSubmitting ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default InviteStaffModal;
